"use client";

import { useState, useRef, useEffect } from "react";
import { Globe } from "lucide-react";
import { LOCALE_LABELS, SUPPORTED_LOCALES, type SupportedLocale } from "@/i18n/routing";

/**
 * Language switcher dropdown for the footer.
 * Sets the NEXT_LOCALE cookie and reloads the page.
 */
export default function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<SupportedLocale>("en-GB");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Read current locale from cookie on mount
  useEffect(() => {
    const match = document.cookie.match(/NEXT_LOCALE=([^;]+)/);
    if (match && SUPPORTED_LOCALES.includes(match[1] as SupportedLocale)) {
      setCurrent(match[1] as SupportedLocale);
    } else {
      // Fallback: read from <html lang>
      const htmlLang = document.documentElement.lang;
      if (SUPPORTED_LOCALES.includes(htmlLang as SupportedLocale)) {
        setCurrent(htmlLang as SupportedLocale);
      }
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  function switchLocale(locale: SupportedLocale) {
    // Set cookie for 1 year
    document.cookie = `NEXT_LOCALE=${locale}; path=/; max-age=31536000; SameSite=Lax`;
    setCurrent(locale);
    setOpen(false);
    // Reload to apply new locale
    window.location.reload();
  }

  const currentLabel = LOCALE_LABELS[current];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition"
        aria-label="Change language"
        aria-expanded={open}
      >
        <Globe className="w-4 h-4" />
        <span>{currentLabel.flag} {currentLabel.name}</span>
      </button>

      {open && (
        <div className="absolute bottom-full mb-2 left-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[180px] z-50">
          {SUPPORTED_LOCALES.map((locale) => {
            const label = LOCALE_LABELS[locale];
            const isActive = locale === current;
            return (
              <button
                key={locale}
                onClick={() => switchLocale(locale)}
                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition ${
                  isActive
                    ? "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 font-medium"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <span>{label.flag}</span>
                <span>{label.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
